/**
 * Base class for every error the SDK throws.
 *
 * Catch this if you want a single instanceof check that covers any failure
 * from the client; subclasses below let you discriminate the cause when you
 * need to.
 */
export class ProxyScrapeError extends Error {
  override name = 'ProxyScrapeError';
  // Use the native Error.cause field rather than our own parallel property,
  // which would (correctly) trip TS's noImplicitOverride. We just pipe the
  // caller's cause into the standard ErrorOptions and stay structural.
  constructor(message: string, cause?: unknown) {
    super(message, cause === undefined ? undefined : { cause });
  }
}

/**
 * Thrown when the API returns 401/403. The message echoes the upstream body
 * when available so callers don't have to inspect `response` to debug.
 */
export class AuthError extends ProxyScrapeError {
  override name = 'AuthError';
  constructor(
    message: string,
    public readonly status: number,
    cause?: unknown,
  ) {
    super(message, cause);
  }
}

/**
 * Thrown after retries are exhausted on a 429 or after the API's documented
 * rate-limit codes (433 hourly / 434 daily / 432 per-second) come back. The
 * `retryAfterSeconds` is read from the Retry-After header when present.
 */
export class RateLimitError extends ProxyScrapeError {
  override name = 'RateLimitError';
  constructor(
    message: string,
    public readonly status: number,
    public readonly retryAfterSeconds: number | null,
    cause?: unknown,
  ) {
    super(message, cause);
  }
}

/**
 * Thrown for non-recoverable 4xx responses (validation errors, malformed
 * params, etc.). 5xx is treated separately as transient and retried by the
 * HTTP wrapper.
 */
export class BadRequestError extends ProxyScrapeError {
  override name = 'BadRequestError';
  constructor(
    message: string,
    public readonly status: number,
    cause?: unknown,
  ) {
    super(message, cause);
  }
}

/**
 * Wraps fetch-level failures (DNS, TCP, TLS, AbortError, etc.) so callers
 * can distinguish "the API said no" from "we couldn't reach the API".
 */
export class NetworkError extends ProxyScrapeError {
  override name = 'NetworkError';
}
